import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CollaborationPanel = () => {
  const [selectedTab, setSelectedTab] = useState('notebooks');
  const [showNotifications, setShowNotifications] = useState(false);

  const researchNotebooks = [
    {
      id: 'notebook_001',
      title: 'Climate Disinformation Analysis',
      description: 'Collaborative research on Green Deal misinformation patterns',
      collaborators: [
        { name: 'Dr. Maria Schmidt', avatar: 'https://randomuser.me/api/portraits/women/32.jpg', status: 'online' },
        { name: 'Jean Dubois', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', status: 'offline' },
        { name: 'Dr. Anna Kowalski', avatar: 'https://randomuser.me/api/portraits/women/28.jpg', status: 'online' }
      ],
      lastActivity: new Date(Date.now() - 1800000),
      status: 'active',
      progress: 75,
      sections: 12,
      comments: 8,
      tags: ['climate', 'policy', 'analysis']
    },
    {
      id: 'notebook_002',
      title: 'Deepfake Detection Methodology',
      description: 'Technical documentation for AI-generated content identification',
      collaborators: [
        { name: 'Prof. Alessandro Rossi', avatar: 'https://randomuser.me/api/portraits/men/52.jpg', status: 'online' },
        { name: 'Dr. Sophie Laurent', avatar: 'https://randomuser.me/api/portraits/women/38.jpg', status: 'away' }
      ],
      lastActivity: new Date(Date.now() - 3600000),
      status: 'review',
      progress: 90,
      sections: 8,
      comments: 15,
      tags: ['deepfake', 'AI', 'methodology']
    },
    {
      id: 'notebook_003',
      title: 'Healthcare Misinformation Network',
      description: 'Mapping anti-vaccination content distribution channels',
      collaborators: [
        { name: 'Dr. Hans Mueller', avatar: 'https://randomuser.me/api/portraits/men/41.jpg', status: 'offline' },
        { name: 'Dr. Elena Popov', avatar: 'https://randomuser.me/api/portraits/women/35.jpg', status: 'online' }
      ],
      lastActivity: new Date(Date.now() - 7200000),
      status: 'draft',
      progress: 45,
      sections: 6,
      comments: 3,
      tags: ['health', 'network', 'vaccination']
    }
  ];

  const pendingPublications = [
    {
      id: 'pub_001',
      title: 'EU Climate Policy Misinformation Campaign - Final Report',
      type: 'fact-check',
      author: 'Dr. Maria Schmidt',
      reviewer: 'Jean Dubois',
      submittedDate: new Date(Date.now() - 86400000),
      deadline: new Date(Date.now() + 172800000),
      status: 'pending-review',
      priority: 'high',
      wordCount: 2450,
      evidenceAttachments: 12,
      verdict: 'False',
      confidenceScore: 0.87
    },
    {
      id: 'pub_002',
      title: 'Deepfake Video Analysis: Political Figure Impersonation',
      type: 'technical-analysis',
      author: 'Prof. Alessandro Rossi',
      reviewer: 'Dr. Anna Kowalski',
      submittedDate: new Date(Date.now() - 172800000),
      deadline: new Date(Date.now() + 86400000),
      status: 'revision-requested',
      priority: 'critical',
      wordCount: 1890,
      evidenceAttachments: 8,
      verdict: 'Manipulated Content',
      confidenceScore: 0.94
    },
    {
      id: 'pub_003',
      title: 'Healthcare Conspiracy Theory Network Analysis',
      type: 'research-report',
      author: 'Dr. Sophie Laurent',
      reviewer: 'Dr. Hans Mueller',
      submittedDate: new Date(Date.now() - 259200000),
      deadline: new Date(Date.now() + 259200000),
      status: 'approved',
      priority: 'medium',
      wordCount: 3200,
      evidenceAttachments: 15,
      verdict: 'Misleading',
      confidenceScore: 0.76
    }
  ];

  const communicationTools = [
    {
      id: 'tool_001',
      name: 'Crisis Communication Hub',
      description: 'Coordinate responses to emerging threats',
      icon: 'MessageSquare',
      status: 'active',
      notifications: 3,
      lastUsed: new Date(Date.now() - 900000)
    },
    {
      id: 'tool_002',
      name: 'Strategic Alignment Interface',
      description: 'Align messaging across institutions',
      icon: 'Target',
      status: 'active',
      notifications: 1,
      lastUsed: new Date(Date.now() - 1800000)
    },
    {
      id: 'tool_003',
      name: 'Briefing Generator',
      description: 'Create and distribute situation reports',
      icon: 'FileText',
      status: 'idle',
      notifications: 0,
      lastUsed: new Date(Date.now() - 7200000)
    },
    {
      id: 'tool_004',
      name: 'Institutional Channels',
      description: 'Direct communication with government bodies',
      icon: 'Building',
      status: 'active',
      notifications: 2,
      lastUsed: new Date(Date.now() - 3600000)
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'review': return 'text-blue-600 bg-blue-50';
      case 'draft': return 'text-gray-600 bg-gray-50';
      case 'pending-review': return 'text-orange-600 bg-orange-50';
      case 'revision-requested': return 'text-red-600 bg-red-50';
      case 'approved': return 'text-green-600 bg-green-50';
      case 'idle': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const totalNotifications = communicationTools?.reduce((sum, tool) => sum + tool?.notifications, 0);

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} className="text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Collaboration Panel</h3>
              <p className="text-sm text-muted-foreground">Research notebooks, publications, and communication tools</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Bell"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              Notifications ({totalNotifications})
            </Button>
            <Button variant="outline" size="sm" iconName="Settings">
              Settings
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          {[
            { id: 'notebooks', label: 'Research Notebooks', count: researchNotebooks?.length },
            { id: 'publications', label: 'Pending Publications', count: pendingPublications?.length },
            { id: 'communications', label: 'Communication Tools', count: communicationTools?.length }
          ]?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setSelectedTab(tab?.id)}
              className={`
                px-3 py-2 text-sm font-medium rounded-lg transition-smooth
                ${selectedTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              {tab?.label} ({tab?.count})
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {selectedTab === 'notebooks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">Active Research Notebooks</h4>
              <Button variant="outline" size="sm" iconName="Plus">
                New Notebook
              </Button>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {researchNotebooks?.map((notebook) => (
                <div
                  key={notebook?.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h5 className="text-sm font-semibold text-foreground">{notebook?.title}</h5>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(notebook?.status)}`}>
                          {notebook?.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{notebook?.description}</p>
                      <div className="text-xs text-muted-foreground">
                        Last activity: {formatTimeAgo(notebook?.lastActivity)}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" iconName="ExternalLink" />
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex -space-x-2">
                      {notebook?.collaborators?.map((collaborator, index) => (
                        <div key={index} className="relative">
                          <img
                            src={collaborator?.avatar}
                            alt={collaborator?.name}
                            className="w-8 h-8 rounded-full border-2 border-card"
                          />
                          <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-card ${
                            collaborator?.status === 'online' ? 'bg-green-500' :
                            collaborator?.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
                          }`} />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>{notebook?.sections} sections</span>
                      <span>{notebook?.comments} comments</span>
                      <span>{notebook?.progress}% complete</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {notebook?.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex-1 bg-muted rounded-full h-2 mx-4">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${notebook?.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'publications' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">Publications Requiring Review</h4>
              <Button variant="outline" size="sm" iconName="Upload">
                Submit Publication
              </Button>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {pendingPublications?.map((publication) => (
                <div
                  key={publication?.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h5 className="text-sm font-semibold text-foreground">{publication?.title}</h5>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(publication?.status)}`}>
                          {publication?.status?.replace('-', ' ')}
                        </span>
                        <Icon name="AlertTriangle" size={14} className={getPriorityColor(publication?.priority)} />
                      </div>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-2">
                        <span>Author: {publication?.author}</span>
                        <span>Reviewer: {publication?.reviewer}</span>
                        <span>Type: {publication?.type}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Submitted: {formatTimeAgo(publication?.submittedDate)}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" iconName="Download" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Word Count</div>
                      <div className="text-sm font-medium text-foreground">{publication?.wordCount?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Evidence</div>
                      <div className="text-sm font-medium text-foreground">{publication?.evidenceAttachments} files</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Verdict</div>
                      <div className="text-sm font-medium text-foreground">{publication?.verdict}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Confidence</div>
                      <div className="text-sm font-medium text-foreground">{(publication?.confidenceScore * 100)?.toFixed(0)}%</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      <Icon name="Calendar" size={12} className="inline mr-1" />
                      Deadline: {formatTimeAgo(publication?.deadline)}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">
                        Review
                      </Button>
                      <Button variant="default" size="sm">
                        Approve
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'communications' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">Strategic Communication Tools</h4>
              <Button variant="outline" size="sm" iconName="MessageSquare">
                New Message
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {communicationTools?.map((tool) => (
                <div
                  key={tool?.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        tool?.status === 'active' ? 'bg-green-500/10' : 'bg-gray-500/10'
                      }`}>
                        <Icon name={tool?.icon} size={20} className={
                          tool?.status === 'active' ? 'text-green-500' : 'text-gray-500'
                        } />
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold text-foreground">{tool?.name}</h5>
                        <p className="text-xs text-muted-foreground">{tool?.description}</p>
                      </div>
                    </div>
                    {tool?.notifications > 0 && (
                      <div className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {tool?.notifications}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tool?.status)}`}>
                        {tool?.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Last used: {formatTimeAgo(tool?.lastUsed)}
                      </span>
                    </div>
                    <Button variant="outline" size="sm" iconName="ExternalLink">
                      Open
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborationPanel;